import React from 'react';
import { motion } from 'framer-motion';
import { Sparkles, Heart, Rocket } from 'lucide-react';
const pillars = [{
  icon: Sparkles,
  label: 'Attract',
  desc: 'Show purpose, science, people, momentum.',
  metrics: [{
    label: 'Participation Rate',
    value: '84%',
    trend: '+12%'
  }, {
    label: 'Reach Across Sites',
    value: '11',
    trend: '+4'
  }],
  accent: 'from-red-500/20 to-transparent',
  ring: 'border-red-500/40'
}, {
  icon: Heart,
  label: 'Retain',
  desc: 'Make employees feel seen, heard, part of the story.',
  metrics: [{
    label: 'Sentiment Signals',
    value: 'Positive',
    trend: 'Stable'
  }, {
    label: 'Morale Indicators',
    value: 'High',
    trend: 'Up'
  }],
  accent: 'from-red-600/20 to-transparent',
  ring: 'border-red-500/50'
}, {
  icon: Rocket,
  label: 'Mobilize',
  desc: 'Turn passive comms into active participation.',
  metrics: [{
    label: 'Engagement Depth',
    value: '4m 12s',
    trend: '+45s'
  }, {
    label: 'Repeat Participation',
    value: '62%',
    trend: '+8%'
  }],
  accent: 'from-red-700/20 to-transparent',
  ring: 'border-red-500/60'
}];
export const Slide07Value = () => {
  return <div className="relative w-full h-full flex flex-col bg-slate-950 overflow-hidden px-24 py-16" data-id="element-175">
      <div className="absolute inset-0 bg-gradient-to-tr from-slate-950 via-slate-900 to-slate-950" data-id="element-176" />
      <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-red-900/10 rounded-full blur-[120px]" data-id="element-177" />

      <div className="relative z-10 h-full flex flex-col" data-id="element-178">
        {/* Header */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.8
      }} className="mb-10" data-id="element-179">
          <div className="flex items-center space-x-3 mb-4" data-id="element-180">
            <div className="h-[1px] w-8 bg-red-600" data-id="element-181" />
            <span className="text-red-500 font-semibold tracking-widest uppercase text-xs" data-id="element-182">
              07 — Leadership Value
            </span>
          </div>
          <div className="flex items-end justify-between" data-id="element-183">
            <h2 className="text-5xl font-bold tracking-tight text-white leading-tight max-w-3xl" data-id="element-184">
              More than content.
              <br data-id="element-185" />
              <span className="text-red-500" data-id="element-186">Leadership intelligence.</span>
            </h2>
            <p className="text-base text-slate-400 font-light max-w-md text-right" data-id="element-187">
              Behavioral data mapped to the three strategic outcomes that matter
              most for CSL Vifor today.
            </p>
          </div>
        </motion.div>

        {/* Dashboard frame */}
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.3,
        duration: 0.8
      }} className="flex-1 bg-slate-900/40 border border-slate-800 rounded-2xl p-6 backdrop-blur-md shadow-2xl flex flex-col" data-id="element-188">
          {/* Dashboard chrome */}
          <div className="flex items-center justify-between mb-6 pb-4 border-b border-slate-800" data-id="element-189">
            <div className="flex items-center space-x-3" data-id="element-190">
              <div className="w-2 h-2 rounded-full bg-red-500 animate-pulse" data-id="element-191" />
              <h3 className="text-sm font-medium text-white tracking-wide" data-id="element-192">
                Engagement Intelligence — Live
              </h3>
            </div>
            <div className="flex items-center space-x-2 text-xs text-slate-500 tracking-widest uppercase" data-id="element-193">
              <span data-id="element-194">Attract</span>
              <span className="text-red-500" data-id="element-195">·</span>
              <span data-id="element-196">Retain</span>
              <span className="text-red-500" data-id="element-197">·</span>
              <span data-id="element-198">Mobilize</span>
            </div>
          </div>

          {/* Three pillar columns */}
          <div className="grid grid-cols-3 gap-6 flex-1" data-id="element-199">
            {pillars.map((pillar, index) => {
            const Icon = pillar.icon;
            return <motion.div key={pillar.label} initial={{
              opacity: 0,
              y: 30
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: 0.5 + index * 0.15,
              duration: 0.6
            }} className={`relative bg-slate-950/60 border ${pillar.ring} rounded-xl p-6 overflow-hidden flex flex-col`} data-id="element-200">
                  <div className={`absolute inset-0 bg-gradient-to-b ${pillar.accent} pointer-events-none`} data-id="element-201" />

                  {/* Pillar header */}
                  <div className="relative z-10 flex items-center space-x-3 mb-3" data-id="element-202">
                    <div className="w-10 h-10 rounded-lg bg-slate-900 border border-red-500/30 flex items-center justify-center" data-id="element-203">
                      <Icon className="w-5 h-5 text-red-400" strokeWidth={1.5} data-id="element-204" />
                    </div>
                    <div data-id="element-205">
                      <div className="text-xs uppercase tracking-[0.2em] text-slate-500 mb-0.5" data-id="element-206">
                        Pillar 0{index + 1}
                      </div>
                      <h4 className="text-xl font-bold text-white" data-id="element-207">
                        {pillar.label}
                      </h4>
                    </div>
                  </div>

                  <p className="relative z-10 text-sm text-slate-400 font-light leading-relaxed mb-5 min-h-[40px]" data-id="element-208">
                    {pillar.desc}
                  </p>

                  {/* Metrics */}
                  <div className="relative z-10 space-y-3 mt-auto" data-id="element-209">
                    {pillar.metrics.map(metric => <div key={metric.label} className="flex items-end justify-between border-t border-slate-800/60 pt-3" data-id="element-210">
                        <div data-id="element-211">
                          <p className="text-xs text-slate-500 mb-1" data-id="element-212">
                            {metric.label}
                          </p>
                          <p className="text-2xl font-bold text-white tracking-tight" data-id="element-213">
                            {metric.value}
                          </p>
                        </div>
                        <span className="text-xs font-semibold text-emerald-400 mb-1" data-id="element-214">
                          {metric.trend}
                        </span>
                      </div>)}
                  </div>
                </motion.div>;
          })}
          </div>
        </motion.div>
      </div>
    </div>;
};